import { Alert, Autocomplete, Box, Chip, IconButton, Snackbar, TextField } from "@mui/material";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { addNewTag, getAllTags, removeTag } from "../../utils/RecipeService";

export type TagViewProps = {
    recipeId: number;
    tags: string[] | undefined;
    editable: boolean;
};

const TagView = (props: TagViewProps) => {
    const {t} = useTranslation();
    const [tags, setTags] = useState<string[]>([]);
    const [newTagText, setNewTagText] = useState<string>('');
    const [existingTags, setExistingTags] = useState<string[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    useEffect(() => {
        setTags(props.tags !== undefined ? props.tags : []);
    }, [props]);

    useEffect(() => {
        getAllTags().then((response: string[]) => {
            setExistingTags(response.filter(t => tags.indexOf(t) === -1));
        });
    }, [setExistingTags, tags]);

    const deleteTag = (tag: string) => {
        removeTag(props.recipeId, tag).then(response => {
            if(response.ok) {
                setTags(oldTags => {
                    return oldTags.filter(existingTag => existingTag !== tag);
                });
            } else {
                setErrorMessage(t('couldNotDeleteTag'));
                setSnackbarOpen(true);
            }
        });
    };

    const addTag = useCallback(() => {
        addNewTag(props.recipeId, newTagText).then(response => {
            if(response.ok) {
                setTags((oldTags) => {
                    if(oldTags.indexOf(newTagText) === -1) {
                        const newTags: string[] = [...oldTags];
                        newTags.push(newTagText);
                        return newTags;
                    } else {
                        return oldTags;
                    }
                });
                setNewTagText('');
            } else {
                setErrorMessage(t('couldNotCreateTag'));
                setSnackbarOpen(true);
            }
        });        
    }, [props.recipeId, newTagText, t]);

    useEffect(() => {
        const listener = (event: KeyboardEvent) => {
            if(event.code === 'Enter' || event.code === 'NumpadEnter') {
                addTag();
            }
        };
        document.addEventListener('keydown', listener);
        return () => {
            document.removeEventListener('keydown', listener);
        };
    }, [addTag]);

    return (
        <div>
            <div style={{display: 'flex', flexDirection: 'row', flexFlow: 'row wrap', marginTop: '5px', width: 'fit-content', maxWidth: '600px', justifyContent: 'center'}}>
                { tags.map(tag => {
                    if(props.editable) {
                        return (
                            <Chip label={tag} style={{marginRight: '5px', marginTop: '5px'}} variant="outlined" onDelete={() => deleteTag(tag)} key={tag} />
                        );
                    } else {
                        return (
                            <Chip label={tag} style={{marginRight: '5px', marginTop: '5px'}} variant="outlined" key={tag} />
                        );
                    }
                    })
                }
            </div>
            {props.editable &&
                <div style={{display: 'flex', flexDirection: 'row', marginTop: '10px', maxWidth: '600px', justifyContent: 'center'}}>
                    <Box sx={{display: 'flex', alignItems: 'flex-end'}}>
                        <LocalOfferIcon sx={{color: 'action.active', mr: 1, my: 0.5}} />
                        <Autocomplete freeSolo options={existingTags} getOptionLabel={option => option} onChange={(e, value) => setNewTagText(value!)} value={newTagText}
                        renderInput={(params) => <TextField {...params} label={t('newTag')} variant="standard" onChange={(newText) => setNewTagText(newText.target.value)} value={newTagText} />} style={{minWidth: '250px'}} />
                    </Box>
                    <IconButton aria-label="add" size="large" style={{marginLeft: '10px'}} onClick={addTag}>
                        <AddIcon fontSize="inherit" />
                    </IconButton>
                </div>
            }

            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={(e) => setSnackbarOpen(false)}
            >
                 <Alert onClose={(e) => setSnackbarOpen(false)} severity="error" variant="filled" sx={{ width: '100%' }}>
                    {errorMessage}
                 </Alert>
            </Snackbar>
        </div>
    );
};

export default TagView;